<template>
  <div class="admin-settings p-4 sm:p-6 lg:p-8">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-900">{{ title }}</h1>
      <p class="text-gray-600 mt-1">Manage global application settings</p>
    </div>

    <!-- Form -->
    <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
      <form @submit.prevent="submit" enctype="multipart/form-data">
        <div class="space-y-8">
          <!-- Default Card Images Section -->
          <div class="border-b border-gray-200 pb-6">
            <h2 class="text-xl font-semibold text-gray-800 mb-4">Default Card Images</h2>
            <p class="text-sm text-gray-600 mb-6">Set default logo and background images that will be used for new cards when no custom images are uploaded.</p>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Default Card Logo -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Default Card Logo
                  <span class="text-xs text-gray-500 font-normal ml-1">(Recommended: 200x200px, PNG/JPG)</span>
                </label>
                <div class="space-y-3">
                  <div v-if="defaultCardLogo" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <img :src="defaultCardLogo" alt="Current default logo" class="w-16 h-16 object-contain border border-gray-300 rounded" />
                    <div class="flex-1">
                      <p class="text-sm font-medium text-gray-700">Current Logo</p>
                      <p class="text-xs text-gray-500">{{ defaultCardLogo }}</p>
                    </div>
                  </div>
                  <div class="relative">
                    <input
                      ref="logoInput"
                      type="file"
                      accept="image/*"
                      @change="handleLogoChange"
                      class="hidden"
                    />
                    <label
                      for="logo-upload"
                      @click.prevent="$refs.logoInput.click()"
                      class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-green-400 transition-colors"
                    >
                      <div class="flex flex-col items-center justify-center pt-2 pb-2">
                        <svg class="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p class="text-sm text-gray-600">
                          <span class="font-semibold text-green-600">Click to upload</span> or drag and drop
                        </p>
                        <p class="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                      </div>
                    </label>
                    <div v-if="form.default_card_logo" class="mt-2">
                      <p class="text-xs text-green-600">New logo selected: {{ form.default_card_logo.name }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Default Card Background -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Default Card Background
                  <span class="text-xs text-gray-500 font-normal ml-1">(Recommended: 1200x800px, PNG/JPG)</span>
                </label>
                <div class="space-y-3">
                  <div v-if="defaultCardBackground" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <img :src="defaultCardBackground" alt="Current default background" class="w-24 h-16 object-cover border border-gray-300 rounded" />
                    <div class="flex-1">
                      <p class="text-sm font-medium text-gray-700">Current Background</p>
                      <p class="text-xs text-gray-500">{{ defaultCardBackground }}</p>
                    </div>
                  </div>
                  <div class="relative">
                    <input
                      ref="backgroundInput"
                      type="file"
                      accept="image/*"
                      @change="handleBackgroundChange"
                      class="hidden"
                    />
                    <label
                      for="background-upload"
                      @click.prevent="$refs.backgroundInput.click()"
                      class="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-green-400 transition-colors"
                    >
                      <div class="flex flex-col items-center justify-center pt-2 pb-2">
                        <svg class="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p class="text-sm text-gray-600">
                          <span class="font-semibold text-green-600">Click to upload</span> or drag and drop
                        </p>
                        <p class="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                      </div>
                    </label>
                    <div v-if="form.default_card_background" class="mt-2">
                      <p class="text-xs text-green-600">New background selected: {{ form.default_card_background.name }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Other Settings Section (if any) -->
          <div v-if="Object.keys(settings).length > 0" class="border-b border-gray-200 pb-6">
            <h2 class="text-xl font-semibold text-gray-800 mb-4">Other Settings</h2>
            <div class="space-y-4">
              <div v-for="(setting, key) in settings" :key="key" v-if="key !== 'default_card_logo' && key !== 'default_card_background'">
                <label class="block text-sm font-medium text-gray-700 mb-2">{{ setting.name }}</label>
                <input
                  v-if="setting.type === 'text'"
                  v-model="form[key]"
                  type="text"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <textarea
                  v-else-if="setting.type === 'textarea'"
                  v-model="form[key]"
                  rows="4"
                  class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                ></textarea>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
            <button
              type="submit"
              :disabled="form.processing"
              class="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-700"
            >
              {{ form.processing ? 'Saving...' : 'Save Settings' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { useForm } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,
  props: {
    title: {
      type: String,
      default: 'Global Settings',
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
    defaultCardLogo: {
      type: String,
      default: '/images/card/no-image.png',
    },
    defaultCardBackground: {
      type: String,
      default: '/images/card/card-bg.jpg',
    },
  },
  data() {
    return {
      form: useForm({
        default_card_logo: null,
        default_card_background: null,
        ...this.settings,
      }),
    }
  },
  methods: {
    handleLogoChange(event) {
      const file = event.target.files[0]
      if (file) {
        this.form.default_card_logo = file
      }
    },
    handleBackgroundChange(event) {
      const file = event.target.files[0]
      if (file) {
        this.form.default_card_background = file
      }
    },
    submit() {
      this.form.post(this.route('global.update'), {
        forceFormData: true,
        onSuccess: () => {
          if (window.$toast) {
            window.$toast.success('Settings updated successfully!')
          }
          // Reset file inputs
          this.form.default_card_logo = null
          this.form.default_card_background = null
          if (this.$refs.logoInput) {
            this.$refs.logoInput.value = ''
          }
          if (this.$refs.backgroundInput) {
            this.$refs.backgroundInput.value = ''
          }
        },
        onError: (errors) => {
          if (window.$toast) {
            window.$toast.error('Failed to update settings. Please try again.')
          }
        },
      })
    },
  },
}
</script>

